import { useEffect, useState } from "react";
import {
  adminListLegislators, adminCreateLegislator, adminUpdateLegislator,
  adminDeleteLegislator, adminRecordVote, adminDeleteVote,
  getBills,
} from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Landmark, Plus, Search, Users as UsersIcon, Trash2,
  CheckCircle2, XCircle, MinusCircle, X,
} from "lucide-react";

const BLANK = {
  name: "", chamber: "House", district: "", party: "", email: "", phone: "",
  photo_url: "", capitol_address: "", committees: [], next_election: "", bio: "",
};

function ScoreBadge({ card }) {
  const s = card?.displayed_score;
  if (s === null || s === undefined) {
    return <span className="text-xs text-stone-400 italic">No votes yet</span>;
  }
  const color = s >= 75 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : s >= 50 ? "bg-amber-100  text-amber-800  border-amber-200"
              :           "bg-red-100    text-red-800    border-red-200";
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center px-2 py-1 border font-mono text-sm ${color}`} data-testid="score-badge">
        {s}%
      </span>
      {card?.is_overridden && (
        <span className="text-[10px] uppercase tracking-widest text-stone-500">Manual</span>
      )}
    </div>
  );
}

function EditDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || BLANK);
  const [committeesText, setCommitteesText] = useState((initial?.committees || []).join(", "));
  const isEdit = !!initial?.id;

  useEffect(() => {
    setForm(initial || BLANK);
    setCommitteesText((initial?.committees || []).join(", "));
  }, [initial]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.name || !form.district) { toast.error("Name and district required"); return; }
    const body = {
      ...form,
      committees: committeesText.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      const r = isEdit
        ? await adminUpdateLegislator(initial.id, body)
        : await adminCreateLegislator(body);
      toast.success(isEdit ? "Legislator updated" : "Legislator added");
      onSaved(r);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="legislator-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ccc-plum">
            {isEdit ? "Edit legislator" : "Add legislator"}
          </DialogTitle>
          <DialogDescription>Fields marked * are required.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <label className="text-xs text-stone-600 uppercase tracking-widest">Name *</label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="legislator-name" />
          </div>
          <div>
            <label className="text-xs text-stone-600 uppercase tracking-widest">District *</label>
            <Input value={form.district} onChange={e => set("district", e.target.value)} placeholder="HD-1 / SD-25" data-testid="legislator-district" />
          </div>
          <div>
            <label className="text-xs text-stone-600 uppercase tracking-widest">Chamber</label>
            <select value={form.chamber} onChange={e => set("chamber", e.target.value)}
              className="w-full border border-stone-300 h-9 px-2 bg-white" data-testid="legislator-chamber">
              <option>House</option><option>Senate</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-stone-600 uppercase tracking-widest">Party</label>
            <Input value={form.party} onChange={e => set("party", e.target.value)} placeholder="D / R / U" />
          </div>
          <div>
            <label className="text-xs text-stone-600 uppercase tracking-widest">Email</label>
            <Input value={form.email} onChange={e => set("email", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-stone-600 uppercase tracking-widest">Phone</label>
            <Input value={form.phone} onChange={e => set("phone", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-stone-600 uppercase tracking-widest">Committees (comma-separated)</label>
            <Input value={committeesText} onChange={e => setCommitteesText(e.target.value)} placeholder="Judiciary, Health & Human Services" data-testid="legislator-committees" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-stone-600 uppercase tracking-widest">Photo URL</label>
            <Input value={form.photo_url} onChange={e => set("photo_url", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-stone-600 uppercase tracking-widest">Bio</label>
            <Textarea value={form.bio || ""} onChange={e => set("bio", e.target.value)} rows={3} />
          </div>
          {isEdit && (
            <div className="col-span-2 border-t border-stone-200 pt-3">
              <label className="text-xs text-stone-600 uppercase tracking-widest">Alignment score override (0-100, or -1 to clear)</label>
              <Input
                type="number" min={-1} max={100}
                value={form.alignment_score_override ?? ""}
                onChange={e => set("alignment_score_override", e.target.value === "" ? null : parseInt(e.target.value))}
                data-testid="legislator-override"
              />
              <div className="text-xs text-stone-500 mt-1">Leave blank to auto-compute from bill votes.</div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-stone-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="legislator-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecordVoteRow({ legId, bills, existingVotes, onRecorded }) {
  const [bid, setBid] = useState("");
  const [vote, setVote] = useState("yes");
  const [source, setSource] = useState("");
  const submit = async () => {
    if (!bid) { toast.error("Pick a bill"); return; }
    try {
      await adminRecordVote(legId, { bill_id: bid, vote, source });
      toast.success("Vote recorded");
      setBid(""); setVote("yes"); setSource("");
      onRecorded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid grid-cols-6 gap-2 items-end p-3 border border-stone-200 bg-stone-50">
      <div className="col-span-3">
        <label className="text-[11px] text-stone-500 uppercase tracking-widest">Bill</label>
        <select value={bid} onChange={e => setBid(e.target.value)}
          className="w-full border border-stone-300 h-9 px-2 bg-white text-sm" data-testid="vote-bill-select">
          <option value="">Select bill…</option>
          {bills.map(b => (
            <option key={b.id} value={b.id}>{b.number} · {b.position} · {b.title.slice(0, 40)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[11px] text-stone-500 uppercase tracking-widest">Vote</label>
        <select value={vote} onChange={e => setVote(e.target.value)}
          className="w-full border border-stone-300 h-9 px-2 bg-white text-sm" data-testid="vote-value">
          <option value="yes">Yes</option>
          <option value="no">No</option>
          <option value="excused">Excused</option>
          <option value="not_voting">Not voting</option>
        </select>
      </div>
      <div className="col-span-2">
        <label className="text-[11px] text-stone-500 uppercase tracking-widest">Source (optional)</label>
        <Input value={source} onChange={e => setSource(e.target.value)} className="h-9" placeholder="Journal / date" />
      </div>
      <div className="col-span-6 flex justify-end">
        <Button size="sm" onClick={submit} data-testid="vote-record">Record vote</Button>
      </div>
    </div>
  );
}

function VoteIcon({ vote, ccc }) {
  const match =
    (ccc === "Support" && vote === "yes") ||
    (ccc === "Oppose"  && vote === "no");
  if (vote === "yes") return <span className={match ? "text-emerald-600" : "text-red-500"}><CheckCircle2 size={16} /></span>;
  if (vote === "no")  return <span className={match ? "text-emerald-600" : "text-red-500"}><XCircle size={16} /></span>;
  return <span className="text-stone-400"><MinusCircle size={16} /></span>;
}

function LegislatorDrawer({ leg, bills, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  if (!leg) return null;
  return (
    <Sheet open={!!leg} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="legislator-drawer">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-serif text-2xl text-ccc-plum flex items-center gap-3">
            {leg.photo_url ? (
              <img src={leg.photo_url} alt={leg.name} className="w-10 h-10 rounded-full object-cover"/>
            ) : (
              <div className="w-10 h-10 rounded-full bg-ccc-plum/10 flex items-center justify-center text-ccc-plum"><Landmark size={18}/></div>
            )}
            {leg.name}
          </SheetTitle>
          <SheetDescription>
            {leg.chamber} · {leg.district}{leg.party && ` · ${leg.party}`}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          <div className="border border-stone-200 bg-stone-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-ccc-purple">CCC alignment</div>
              <ScoreBadge card={leg.scorecard} />
            </div>
            <div className="text-sm text-stone-700">
              {leg.scorecard?.matches ?? 0} of {leg.scorecard?.total ?? 0} tracked votes align with CCC.
              {" "}Override in Edit to force a manual score.
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="btn-edit-legislator">Edit profile</Button>
            <a href={`/admin/legislators/${leg.id}/brief`} target="_blank" rel="noreferrer" data-testid="btn-open-brief">
              <Button size="sm">
                <Landmark size={14} className="mr-1.5"/>Open constituent brief
              </Button>
            </a>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-widest text-ccc-purple mb-2">Record a vote</h3>
            <RecordVoteRow legId={leg.id} bills={bills} existingVotes={leg.votes} onRecorded={onChanged} />
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-widest text-ccc-purple mb-2">Voting record</h3>
            {(!leg.votes || leg.votes.length === 0) && (
              <div className="text-xs text-stone-400 italic">No votes recorded yet.</div>
            )}
            <div className="divide-y divide-stone-100 border border-stone-200">
              {leg.votes?.map(v => (
                <div key={v.id} className="flex items-center gap-3 p-3 hover:bg-stone-50" data-testid={`vote-row-${v.bill_id}`}>
                  <VoteIcon vote={v.vote} ccc={v.ccc_position} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800">
                      {v.bill_number} <span className="text-stone-400 font-normal">· CCC {v.ccc_position || "—"}</span>
                    </div>
                    <div className="text-xs text-stone-500 truncate">{v.bill_title}</div>
                    {v.source && <div className="text-xs text-stone-400 mt-0.5">{v.source}</div>}
                  </div>
                  <button
                    onClick={async () => { await adminDeleteVote(leg.id, v.bill_id); toast.success("Vote removed"); onChanged(); }}
                    className="text-stone-400 hover:text-red-600"
                    aria-label="Delete vote"
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {leg.bio && (
            <div>
              <h3 className="text-xs uppercase tracking-widest text-ccc-purple mb-2">Bio</h3>
              <div className="text-sm text-stone-700 whitespace-pre-wrap">{leg.bio}</div>
            </div>
          )}
        </div>

        <EditDialog open={editing} initial={leg} onClose={() => setEditing(false)} onSaved={onChanged} />
      </SheetContent>
    </Sheet>
  );
}

export default function Legislators() {
  const [legs, setLegs] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [chamber, setChamber] = useState("");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, b] = await Promise.all([
        adminListLegislators({ q, chamber: chamber || undefined }),
        getBills(),
      ]);
      setLegs(l.legislators || []);
      setBills(b || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, chamber]);

  const refreshSelected = async () => {
    await load();
    if (selected) {
      const fresh = await adminListLegislators({ q: selected.name });
      const match = (fresh.legislators || []).find(l => l.id === selected.id);
      if (match) setSelected(match);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this legislator? Their voting record will be removed too.")) return;
    try { await adminDeleteLegislator(id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-legislators-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-ccc-gold">Legislative</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Legislator Tracker.</h1>
          <div className="text-sm text-stone-600 mt-1 max-w-2xl">
            Profiles + CCC alignment score = matches on Support/Oppose votes ÷ total tracked votes. Manual override available.
          </div>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="btn-add-legislator">
          <Plus size={14} className="mr-1.5"/> Add legislator
        </Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-stone-400"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or district…"
            className="pl-8 w-64 h-9" data-testid="legislators-search"/>
        </div>
        <select value={chamber} onChange={e => setChamber(e.target.value)}
          className="border border-stone-300 h-9 px-2 bg-white text-sm" data-testid="legislators-chamber-filter">
          <option value="">All chambers</option>
          <option value="House">House</option>
          <option value="Senate">Senate</option>
        </select>
      </div>

      <div className="bg-white border border-stone-200">
        {loading && <div className="p-8 text-center text-stone-400 text-sm">Loading legislators…</div>}
        {!loading && legs.length === 0 && (
          <div className="p-8 text-center text-stone-400 text-sm">No legislators. Add one to start tracking votes.</div>
        )}
        {!loading && legs.map(l => (
          <div key={l.id} className="border-b border-stone-100 hover:bg-stone-50 flex items-center gap-4 p-4"
               data-testid={`legislator-row-${l.id}`}>
            {l.photo_url ? (
              <img src={l.photo_url} alt="" className="w-10 h-10 rounded-full object-cover"/>
            ) : (
              <div className="w-10 h-10 rounded-full bg-ccc-plum/10 text-ccc-plum flex items-center justify-center"><Landmark size={16}/></div>
            )}
            <button onClick={() => setSelected(l)} className="flex-1 text-left min-w-0">
              <div className="text-sm font-medium text-stone-800 flex items-center gap-2">
                {l.name}
                {l.party && <span className="text-xs text-stone-400">({l.party})</span>}
              </div>
              <div className="text-xs text-stone-500">{l.chamber} · {l.district} {l.committees?.length ? `· ${l.committees.length} committee${l.committees.length===1?"":"s"}` : ""}</div>
            </button>
            <div className="hidden sm:block">
              <ScoreBadge card={l.scorecard} />
            </div>
            <button onClick={() => del(l.id)} className="text-stone-400 hover:text-red-600" aria-label="Delete">
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>

      <LegislatorDrawer leg={selected} bills={bills} onClose={() => setSelected(null)} onChanged={refreshSelected} />
      <EditDialog open={creating} initial={null} onClose={() => setCreating(false)} onSaved={load} />
    </div>
  );
}
